import { useEffect, useState, useSyncExternalStore } from "react";
import { buildCmmdRuntimeEnvironmentId } from "../../environments/cmmdRuntimeEnvironmentId";

import { EnvironmentId } from "@cmmd-forge/contracts";

import {
  ensureRelayRuntimeEnvironmentConnection,
  readEnvironmentConnection,
  subscribeEnvironmentConnections,
} from "../../environments/runtime/service";
import { useSavedEnvironmentRuntimeStore } from "../../environments/runtime/savedEnvironmentRuntimeStore";
import {
  classifyRemoteRuntimeConnectionError,
  deriveRemoteRuntimeConnectionState,
  observeRemoteRuntimeConnection,
  recordRemoteRuntimeConnectionFailure,
  recordRemoteRuntimeConnectionRetry,
  type RemoteRuntimeAvailability,
  type RemoteRuntimeConnectionAttemptState,
  type RemoteRuntimeConnectionState,
} from "./remoteRuntimeThreadConnection.logic";
import type { RemoteRuntimeThreadRouteTarget } from "./remoteRuntimeThreadRoute.logic";

export interface RemoteRuntimeThreadConnectionController {
  readonly state: RemoteRuntimeConnectionState;
  readonly retry: () => void;
}

interface RemoteRuntimeConnectionTarget {
  readonly runtimeId: string | null;
  readonly runtimeName: string;
  readonly availability: RemoteRuntimeAvailability;
  readonly targetKey: string;
  readonly environmentId: EnvironmentId | null;
}

function resolveRemoteRuntimeConnectionTarget(
  target: RemoteRuntimeThreadRouteTarget | null,
): RemoteRuntimeConnectionTarget {
  if (target === null) {
    return {
      runtimeId: null,
      runtimeName: "Desktop runtime",
      availability: "offline",
      targetKey: "",
      environmentId: null,
    };
  }

  const environmentId = EnvironmentId.make(
    target.connectionKind === "cloud"
      ? buildCmmdRuntimeEnvironmentId(target.runtimeId)
      : target.runtimeId,
  );
  return {
    runtimeId: target.runtimeId,
    runtimeName: target.runtimeDisplayName ?? "Desktop runtime",
    availability: target.availability,
    targetKey: `${target.runtimeId}:${target.availability}`,
    environmentId,
  };
}

function openRemoteRuntimeConnection(input: {
  readonly runtimeId: string | null;
  readonly environmentId: EnvironmentId | null;
  readonly runtimeName: string;
  readonly availability: RemoteRuntimeAvailability;
  readonly connected: boolean;
  readonly activeAttempt: number;
}): Promise<unknown> | null {
  if (
    input.runtimeId === null ||
    input.environmentId === null ||
    input.availability !== "available" ||
    input.connected
  )
    return null;
  const existing = readEnvironmentConnection(input.environmentId);
  if (existing !== null && input.activeAttempt === 0) return null;
  return existing === null
    ? ensureRelayRuntimeEnvironmentConnection({
        runtimeId: input.environmentId,
        label: input.runtimeName,
      })
    : existing.reconnect().then(() => existing.ensureBootstrapped());
}

function useRemoteRuntimeConnectionAttempt(input: {
  readonly runtimeId: string | null;
  readonly environmentId: EnvironmentId | null;
  readonly runtimeName: string;
  readonly availability: RemoteRuntimeAvailability;
  readonly targetKey: string;
  readonly connected: boolean;
  readonly executionIntent: boolean;
}): {
  readonly attemptState: RemoteRuntimeConnectionAttemptState;
  readonly retry: () => void;
} {
  const [attemptState, setAttemptState] = useState<RemoteRuntimeConnectionAttemptState>(() => ({
    targetKey: input.targetKey,
    attempt: 0,
    failure: null,
  }));
  const activeAttempt = attemptState.targetKey === input.targetKey ? attemptState.attempt : 0;

  useEffect(() => {
    if (!input.executionIntent) return;
    const operation = openRemoteRuntimeConnection({
      runtimeId: input.runtimeId,
      environmentId: input.environmentId,
      runtimeName: input.runtimeName,
      availability: input.availability,
      connected: input.connected,
      activeAttempt,
    });
    if (operation === null) return;

    let active = true;
    void operation.catch((error: unknown) => {
      if (!active) return;
      const failure = classifyRemoteRuntimeConnectionError(error);
      setAttemptState((current) =>
        recordRemoteRuntimeConnectionFailure({
          current,
          activeTargetKey: input.targetKey,
          activeAttempt,
          failure,
        }),
      );
    });
    return () => {
      active = false;
    };
  }, [
    activeAttempt,
    input.environmentId,
    input.availability,
    input.connected,
    input.executionIntent,
    input.runtimeId,
    input.runtimeName,
    input.targetKey,
  ]);

  const retry = () => {
    if (input.runtimeId === null || input.availability !== "available") return;
    setAttemptState((current) =>
      recordRemoteRuntimeConnectionRetry({ current, targetKey: input.targetKey }),
    );
  };

  return { attemptState, retry };
}

export function useRemoteRuntimeThreadConnection(
  target: RemoteRuntimeThreadRouteTarget | null,
  options?: { readonly executionIntent?: boolean },
): RemoteRuntimeThreadConnectionController {
  const { runtimeId, runtimeName, availability, targetKey, environmentId } =
    resolveRemoteRuntimeConnectionTarget(target);
  const registeredConnection = useSyncExternalStore(
    subscribeEnvironmentConnections,
    () => (environmentId === null ? null : readEnvironmentConnection(environmentId)),
    () => null,
  );
  const runtimeState = useSavedEnvironmentRuntimeStore((store) =>
    environmentId === null ? null : (store.byId[environmentId] ?? null),
  );
  const initialObservation = observeRemoteRuntimeConnection({
    attemptState: { targetKey, attempt: 0, failure: null },
    targetKey,
    hasRegisteredConnection: registeredConnection !== null,
    transportState: runtimeState?.connectionState ?? null,
    transportError: runtimeState?.lastError ?? null,
  });
  const { attemptState, retry } = useRemoteRuntimeConnectionAttempt({
    runtimeId,
    environmentId,
    runtimeName,
    availability,
    targetKey,
    connected: initialObservation.connected,
    executionIntent: options?.executionIntent ?? false,
  });
  const observation = observeRemoteRuntimeConnection({
    attemptState,
    targetKey,
    hasRegisteredConnection: registeredConnection !== null,
    transportState: runtimeState?.connectionState ?? null,
    transportError: runtimeState?.lastError ?? null,
  });
  const state = deriveRemoteRuntimeConnectionState({
    availability,
    connected: observation.connected,
    attempt: observation.effectiveAttempt + (observation.transportRetrying ? 1 : 0),
    failure: observation.failure,
  });

  return {
    state:
      options?.executionIntent === false && state.kind === "connecting" ? { kind: "idle" } : state,
    retry,
  };
}
